import React, { useState } from 'react';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * DiseaseTrackerPattern - Pattern for progressive disease stage tracking (Serpent's Sickness)
 *
 * Config properties used:
 * - diseaseName: display name
 * - description: disease description
 * - stages: array of stage definitions
 * - cure: cure information
 * - progressTrigger: how disease progresses
 */
const DiseaseTrackerPattern = ({
  config,
  character,
  updateCharacter,
  onClose,
}) => {
  const stages = config.stages || [];
  const currentStageId = character.diseaseStage || 'healthy';
  const currentStageIndex = stages.findIndex(s => s.id === currentStageId);
  const currentStage = stages[currentStageIndex] || stages[0];

  const handleProgress = () => {
    if (currentStageIndex < stages.length - 1) {
      const nextStage = stages[currentStageIndex + 1];
      updateCharacter({
        ...character,
        diseaseStage: nextStage.id,
      });
    }
  };

  const handleCure = () => {
    updateCharacter({
      ...character,
      diseaseStage: 'healthy',
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-1">
          🐍 {config.diseaseName || 'Disease Tracker'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {config.description}
        </p>
      </div>

      {/* Stage Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-gray-500">
          {stages.map((stage, i) => (
            <span
              key={stage.id}
              className={i <= currentStageIndex ? 'text-red-500 font-medium' : ''}
            >
              {stage.icon}
            </span>
          ))}
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 h-2 rounded-full transition-all"
            style={{ width: `${(currentStageIndex / (stages.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Current Stage Display */}
      <div className="bg-red-900/20 p-6 rounded-lg border border-red-600 text-center">
        <p className="text-4xl mb-2">{currentStage?.icon}</p>
        <p className="text-xl font-bold text-red-400">{currentStage?.name}</p>
        <p className="text-sm text-gray-400 mt-2">
          {currentStage?.description}
        </p>
        {currentStage?.mechanicalEffects && (
          <div className="mt-3 text-xs text-red-300">
            Effects: {Object.entries(currentStage.mechanicalEffects)
              .map(([key, val]) => `${key}: ${val > 0 ? '+' : ''}${val}`)
              .join(', ')}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <DomainCardButton
          onClick={handleProgress}
          disabled={currentStageIndex >= stages.length - 1}
          variant="attack"
          className="flex-1"
        >
          Progress Disease
        </DomainCardButton>
        <DomainCardButton
          onClick={handleCure}
          disabled={currentStageId === 'healthy'}
          variant="activeSuccess"
          className="flex-1"
        >
          Apply Cure
        </DomainCardButton>
      </div>

      {/* Cure Info */}
      {config.cure && (
        <div className="bg-green-900/20 p-3 rounded-lg border border-green-600">
          <p className="text-sm text-green-400">
            <strong>Cure:</strong> {config.cure.name}
          </p>
          <p className="text-xs text-green-300">{config.cure.description}</p>
        </div>
      )}

      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>
    </div>
  );
};

export default DiseaseTrackerPattern;
